import { Active, DataRef, Over } from '@dnd-kit/core';

import { RetrieveOrderSchema, RetrieveProcessSchema, WarehouseFlowSchema } from '@app/models/orders';

import { ColumnDragData } from './components/BoardColumn';
import type { ITask, ITaskDragData } from './components/TaskCard';

type DraggableData = ColumnDragData | ITaskDragData;

export function hasDraggableData<T extends Active | Over>(
  entry: T | null | undefined
): entry is T & {
  data: DataRef<DraggableData>;
} {
  if (!entry) {
    return false;
  }

  const data = entry.data.current;

  if (data?.type === 'Column' || data?.type === 'Task') {
    return true;
  }

  return false;
}

export const mapOrderToTask = (order: RetrieveOrderSchema, warehouseFlowArtifactUid?: string): ITask => {
  const fieldGroups: any = order?.field_groups || {};
  const deliveryInfo = fieldGroups?.delivery_info || fieldGroups?.delivery || {};
  const recipient = deliveryInfo?.recipient || {};
  const address = deliveryInfo?.address || recipient?.address || {};
  const warehouseFlow: WarehouseFlowSchema | null | undefined = warehouseFlowArtifactUid
    ? (order.artifacts as Record<string, any> | null | undefined)?.[warehouseFlowArtifactUid]
    : undefined;

  return {
    columnId: order.current_status_uid || '',
    id: order?.uid || '',
    content: order.uid || '',
    version: order.version || '',
    num: order.num,
    // created_at: order.created_at,
    permissionView: order.$permissions.view,
    available_statuses_uids: order.$available_statuses.map((el) => el.uid),

    cityName: (address?.city_name || address?.as_string || null) as string | null,
    recipientName: (recipient?.name || deliveryInfo?.name || null) as string | null,
    deliveryDate: (deliveryInfo?.delivery_date || null) as string | null,
    warehouseError: warehouseFlow?.last_error_message || null,
  };
};

export const getBoardView = (boardData: RetrieveProcessSchema, tasks: ITask[]) => {
  const tasksByColumnId = tasks.reduce<Record<string, ITask[]>>((acc, task) => {
    acc[task.columnId] = [...(acc[task.columnId] ?? []), task];

    return acc;
  }, {});

  const moduleSettingsByStageUid = boardData.modules.reduce<
    Record<string, { hiddenStatusUids: string[]; moduleUid: string }>
  >((acc, module) => {
    const stageUid = module.info?.stage_uid;

    if (stageUid && module.uid) {
      acc[stageUid] = {
        hiddenStatusUids: module.settings?.hidden_status_uids ?? [],
        moduleUid: module.uid,
      };
    }

    return acc;
  }, {});

  const stages = boardData.stages.map((stage) => {
    const moduleSettings = moduleSettingsByStageUid[stage.uid];
    const hiddenStatusUids = moduleSettings?.hiddenStatusUids ?? [];
    const statuses = stage.statuses.map((status) => {
      const statusTasks = tasksByColumnId[status.uid] ?? [];

      return {
        ...status,
        isHidden: hiddenStatusUids.includes(status.uid),
        tasks: statusTasks,
      };
    });

    return {
      ...stage,
      hiddenStatusUids,
      moduleUid: moduleSettings?.moduleUid,
      statuses,
      total: statuses.reduce((acc, status) => acc + status.tasks.length, 0),
      visibleStatuses: statuses.filter((status) => !status.isHidden),
    };
  });

  return {
    columnsId: stages.flatMap((stage) => stage.visibleStatuses.map((status) => status.uid)),
    stages,
  };
};
